import { Injectable, computed, inject, signal } from '@angular/core';
import {
  Auth,
  User,
  UserCredential,
  AuthCredential,
  AuthError,
  RecaptchaVerifier,
  ConfirmationResult,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  browserPopupRedirectResolver,
  signInWithPhoneNumber,
  linkWithCredential,
  PhoneAuthProvider,
  updatePhoneNumber,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
  verifyBeforeUpdateEmail,
  onAuthStateChanged,
  getAdditionalUserInfo,
  getIdTokenResult,
  signOut as firebaseSignOut
  // Imported exclusively from '@angular/fire/auth' (which re-exports all of 'firebase/auth'),
  // never mixed with a direct 'firebase/auth' import — Vite's dependency pre-bundling can end up
  // creating two separate module instances of 'firebase/auth' when it's imported both directly
  // and indirectly (via @angular/fire), which breaks `instanceof FirebaseError` checks inside the
  // SDK itself. That's exactly what caused EMAIL_EXISTS et al. to surface as a generic
  // "auth/network-request-failed" instead of their real, correctly-mapped error code.
} from '@angular/fire/auth';

// Sourced from the installed firebase/auth SDK's AuthErrorCodes map (server error -> auth/* code).
// Only web-reachable codes are covered here; native-only ones (Cordova, Android/iOS bundle IDs,
// MFA, tenants) are omitted since this app never triggers them.
const ERROR_MESSAGES: Record<string, string> = {
  'auth/email-already-in-use': 'An account with this email already exists. Try logging in instead.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/weak-password': 'Password should be at least 6 characters.',
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/user-not-found': 'No account found with this email.',
  'auth/user-disabled': 'This account has been disabled. Please contact support.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/quota-exceeded': "We've hit our verification limit for now. Please try again later.",
  'auth/popup-closed-by-user': 'Google sign-in was cancelled.',
  'auth/popup-blocked': 'Your browser blocked the sign-in popup. Please allow popups for this site and try again.',
  'auth/cancelled-popup-request': 'Sign-in was cancelled. Please try again.',
  'auth/account-exists-with-different-credential':
    'An account already exists with this email using a different sign-in method. Try logging in that way instead.',
  'auth/invalid-phone-number': 'Please enter a valid 10-digit mobile number.',
  'auth/missing-phone-number': 'Please enter a mobile number.',
  'auth/invalid-verification-code': 'That OTP is incorrect. Please check and try again.',
  'auth/missing-verification-code': 'Please enter the 6-digit code.',
  'auth/code-expired': 'This OTP has expired. Please request a new one.',
  'auth/captcha-check-failed': 'We could not verify you\'re not a robot. Please try again.',
  'auth/missing-app-credential': 'We could not verify you\'re not a robot. Please refresh and try again.',
  'auth/invalid-app-credential': 'This device/browser could not be verified for phone sign-in. Please try again.',
  'auth/network-request-failed': 'Network error. Please check your connection and try again.',
  'auth/timeout': 'The request timed out. Please try again.',
  'auth/credential-already-in-use': 'This is already linked to a different account.',
  'auth/provider-already-linked': 'This account already has an email and password set up.',
  'auth/requires-recent-login': 'For security, please sign in again before retrying this action.',
  'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
  'auth/unauthorized-domain': 'This site is not authorized for sign-in. Please contact support.',
  'auth/internal-error': 'Something went wrong on our end. Please try again.',
  'auth/user-token-expired': 'Your session has expired. Please sign in again.',
  'auth/invalid-user-token': 'Your session is no longer valid. Please sign in again.',
  'auth/user-mismatch': 'This credential does not match the currently signed-in account.',
  'auth/expired-action-code': 'This link has expired. Please request a new one.',
  'auth/invalid-action-code': 'This link is invalid or has already been used.',
  'auth/otp-timeout':
    'This is taking too long — a "confirm you\'re not a robot" popup may have appeared behind this window. Please try again and complete it if it shows up.'
};

const OTP_SEND_TIMEOUT_MS = 45000;

export interface SignInResult {
  user: User;
  /** True only for an account created by this very call (Firebase's own signal, not a proxy). */
  isNewUser: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth = inject(Auth);

  // equal: () => false — Firebase's updateProfile()/reload() mutate the SAME User object in
  // place rather than handing back a new one, so a default Object.is check would see "no change"
  // and skip notifying subscribers (e.g. the header greeting) even after a real update.
  currentUser = signal<User | null>(null, { equal: () => false });

  // Two-letter initials from the display name (falls back to the email's first letter, then to
  // null for a phone-only account with neither) — shared by every avatar in the app (storefront
  // header, admin/business layout) so a signed-in user sees who they are at a glance.
  userInitials = computed(() => {
    const user = this.currentUser();
    if (!user) return null;
    const name = user.displayName?.trim();
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      const initials = parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2);
      return initials.toUpperCase();
    }
    if (user.email) return user.email[0].toUpperCase();
    return null;
  });

  private recaptchaVerifier: RecaptchaVerifier | null = null;
  private recaptchaMount: HTMLElement | null = null;

  constructor() {
    onAuthStateChanged(this.auth, (user) => this.currentUser.set(user));
  }

  async sendPasswordReset(email: string): Promise<void> {
    await sendPasswordResetEmail(this.auth, email);
  }

  async signInWithGoogle(): Promise<SignInResult> {
    // AngularFire's getAuth() omits the popup/redirect resolver by default (to avoid loading
    // iframe.js when it isn't needed) — signInWithPopup needs it passed explicitly here.
    const credential = await signInWithPopup(this.auth, new GoogleAuthProvider(), browserPopupRedirectResolver);
    return this.toSignInResult(credential);
  }

  /**
   * Email/Password, re-added as a public sign-in method (stakeholder request) after being pulled
   * from the UI on 2026-08-30. One call handles both signup and returning sign-in, exactly like
   * signInWithGoogle()/verifyOtp() already do — Firebase itself decides which happened via
   * getAdditionalUserInfo().isNewUser (toSignInResult()), not a mode flag this app tracks. A
   * create attempt that hits an email already registered here retries as a plain sign-in with the
   * same credentials — createUserWithEmailAndPassword's own auth/email-already-in-use is NOT
   * suppressed by email-enumeration protection (unlike verifyBeforeUpdateEmail elsewhere in this
   * file — Google's own docs confirm this specific error stays a reliable signal), so branching on
   * it here is safe. If that retry itself fails (e.g. the email actually belongs to a Google-only
   * account with no password credential), it surfaces as an ordinary wrong-password/invalid-
   * credential error via mapFirebaseAuthError() — a known, accepted gap, not a dead end: the
   * Google button is still right there to sign in the normal way.
   */
  async signInOrSignUpWithEmail(email: string, password: string): Promise<SignInResult> {
    try {
      const credential = await createUserWithEmailAndPassword(this.auth, email, password);
      return this.toSignInResult(credential);
    } catch (err) {
      if ((err as { code?: string })?.code !== 'auth/email-already-in-use') {
        throw err;
      }
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      return this.toSignInResult(credential);
    }
  }

  async resendVerificationEmail(): Promise<void> {
    if (!this.auth.currentUser) {
      // Previously silently resolved here — the caller would show a false-positive "email sent"
      // message even though nothing was actually sent. Throw so EmailVerifyService's catch block
      // surfaces a real error instead.
      throw new Error('No signed-in user to send a verification email to.');
    }
    await sendEmailVerification(this.auth.currentUser);
  }

  /**
   * Prepares the invisible Firebase reCAPTCHA verifier inside the given host element, required
   * before sendOtp(). Safe to call before every OTP send (first send, resend, after "change
   * number", or after a rejected sign-in attempt) — it always tears down whatever came before and
   * builds a completely fresh widget.
   *
   * **Never render into the host element itself — always into a brand-new child node.** grecaptcha
   * keys its internal "already rendered" registry off the exact DOM node, and for an *invisible*
   * widget nothing we can call releases that registration: `RecaptchaVerifier.clear()` explicitly
   * skips all DOM cleanup when `isInvisible` (verified in the installed SDK), and wiping the host's
   * innerHTML removes grecaptcha's children without telling grecaptcha to forget the node. Reusing
   * the host is therefore exactly what produced the recurring
   * "reCAPTCHA has already been rendered in this element". This mirrors what the Firebase SDK
   * itself does for *visible* widgets — `makeRenderPromise()` appends a `guaranteedEmpty` div and
   * renders into that — a step it skips for invisible ones, so we do it here instead.
   *
   * This also replaces the previous "reset the existing widget rather than recreate it" path,
   * which is where "reCAPTCHA client element has been removed" came from: resetting depends on the
   * previously-rendered node still being alive and un-consumed, and neither holds once a widget has
   * completed a verification or Angular has torn the container down on a step/route change. A fresh
   * mount can't hit either failure, so nothing needs to guess whether reuse is safe.
   */
  async prepareRecaptcha(hostId: string): Promise<void> {
    this.disposeRecaptcha();

    const host = document.getElementById(hostId);
    if (!host) {
      throw new Error(`reCAPTCHA host element #${hostId} was not found in the DOM.`);
    }

    host.innerHTML = '';
    const mount = document.createElement('div');
    host.appendChild(mount);
    this.recaptchaMount = mount;

    this.recaptchaVerifier = new RecaptchaVerifier(this.auth, mount, { size: 'invisible' });
    // Render eagerly so a widget/script failure surfaces here, attributed to "preparing", rather
    // than later inside signInWithPhoneNumber where it reads as an OTP-send failure.
    await this.recaptchaVerifier.render();
  }

  /**
   * Tears down the current verifier and every trace of its widget. Always use this rather than
   * assigning `recaptchaVerifier = null` directly — dropping the reference alone leaves the widget
   * registered against its DOM node and Google's challenge iframe/badge orphaned in `document.body`
   * (rendered outside our own container, so clearing that element never removes them). A verifier
   * is treated as strictly single-use.
   */
  disposeRecaptcha(): void {
    try {
      this.recaptchaVerifier?.clear();
    } catch {
      // Already destroyed, or its element is gone — nothing left to release.
    }
    this.recaptchaVerifier = null;
    this.recaptchaMount?.remove();
    this.recaptchaMount = null;
    document.querySelectorAll('iframe[src*="recaptcha/api2/"], .grecaptcha-badge').forEach((el) => el.remove());
  }

  async sendOtp(phoneE164: string): Promise<ConfirmationResult> {
    if (!this.recaptchaVerifier) {
      throw new Error('Call prepareRecaptcha() before sendOtp().');
    }
    const verifier = this.recaptchaVerifier;
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject({ code: 'auth/otp-timeout' }), OTP_SEND_TIMEOUT_MS)
    );
    try {
      return await Promise.race([signInWithPhoneNumber(this.auth, phoneE164, verifier), timeout]);
    } catch (err) {
      // Whatever just failed, fully release this widget rather than only dropping the reference —
      // an un-disposed widget stays registered against its DOM node and breaks the next render.
      this.disposeRecaptcha();
      throw err;
    }
  }

  async verifyOtp(confirmationResult: ConfirmationResult, code: string): Promise<SignInResult> {
    const credential = await confirmationResult.confirm(code);
    // A widget that's completed a verification is spent — release it fully (not just the
    // reference) so the next send on this same page starts from a clean mount. This is the exact
    // path behind the reported repro: verify succeeds, sign-in is then rejected for the wrong
    // portal, and the retry re-renders into a container grecaptcha still had claimed.
    this.disposeRecaptcha();
    return this.toSignInResult(credential);
  }

  /**
   * Sends an OTP to add or *change* the currently signed-in user's phone number — the counterpart
   * to sendOtp(), which is sign-in only and would switch identity out from under an
   * already-authenticated user. Deliberately uses PhoneAuthProvider.verifyPhoneNumber() rather
   * than linkWithPhoneNumber(): linking throws `auth/provider-already-linked` the instant a phone
   * is already attached, so it can only ever add, never change — verifyPhoneNumber() + the
   * confirmPhoneVerification() below (using updatePhoneNumber()) handles both in one path.
   */
  async sendPhoneVerification(phoneE164: string): Promise<string> {
    if (!this.recaptchaVerifier) {
      throw new Error('Call prepareRecaptcha() before sendPhoneVerification().');
    }
    if (!this.auth.currentUser) {
      throw new Error('No signed-in user to verify a phone number for.');
    }
    const verifier = this.recaptchaVerifier;
    const provider = new PhoneAuthProvider(this.auth);
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject({ code: 'auth/otp-timeout' }), OTP_SEND_TIMEOUT_MS)
    );
    try {
      return await Promise.race([provider.verifyPhoneNumber(phoneE164, verifier), timeout]);
    } catch (err) {
      this.disposeRecaptcha();
      throw err;
    }
  }

  /**
   * Confirms the OTP from sendPhoneVerification() and applies it via updatePhoneNumber() — adding
   * or replacing the user's phone number. This is also the real check for "is this number already
   * registered elsewhere": Firebase rejects the credential with `auth/credential-already-in-use`
   * the moment it's already tied to a different account. There's no client-callable lookup to
   * pre-check that instead (same reasoning as `fetchSignInMethodsForEmail` being
   * unreliable/deprecated under email-enumeration protection — see
   * linkGoogleCredentialViaPassword() — Firebase doesn't expose an equivalent for phone either, by
   * the same design), so reacting to this error *is* the correct, reliable check, not a fallback.
   * Phone verification completes synchronously in this same session (unlike email), so the new
   * `phone_number` claim is forced onto a fresh ID token immediately — otherwise the caller's next
   * bootstrap() call would only pick it up after Firebase's own ~1hr token refresh.
   */
  async confirmPhoneVerification(verificationId: string, code: string): Promise<void> {
    if (!this.auth.currentUser) {
      throw new Error('No signed-in user to verify a phone number for.');
    }
    const user = this.auth.currentUser;
    const credential = PhoneAuthProvider.credential(verificationId, code);
    try {
      await updatePhoneNumber(user, credential);
    } finally {
      this.disposeRecaptcha();
    }
    await user.getIdToken(true);
  }

  private toSignInResult(credential: UserCredential): SignInResult {
    return { user: credential.user, isNewUser: getAdditionalUserInfo(credential)?.isNewUser ?? false };
  }

  /**
   * Where to send the user right after a successful sign-in. Reads the `role` custom claim off
   * the current ID token (granted out-of-band, manually, from the backend — this app never sets
   * it) and routes admins to `/admin` instead of the normal post-login destination — admin, when
   * present, always wins.
   *
   * `customerType` is optional and comes from the caller's own `bootstrap()` response, not read
   * here — it's not a Firebase claim (never mirrored, same reasoning as `role` not being mirrored
   * into Postgres). When it's `'B2B'`, a Business customer lands on `/business` instead of the
   * storefront — but only when `defaultUrl` is the plain `'/'` default: an explicit `returnUrl`
   * (e.g. a guard sending them back to a specific page) is intentional and takes priority.
   */
  async getPostSignInRedirect(defaultUrl: string, customerType?: 'B2C' | 'B2B'): Promise<string> {
    if (this.auth.currentUser) {
      const tokenResult = await getIdTokenResult(this.auth.currentUser);
      if (tokenResult.claims['role'] === 'admin') {
        return '/admin';
      }
    }
    if (customerType === 'B2B' && defaultUrl === '/') {
      return '/business';
    }
    return defaultUrl;
  }

  /**
   * Queues Firebase's own verification email for the new address — never sets a password, never
   * changes `auth.currentUser.email` immediately (Firebase only applies the change once the user
   * clicks the link, typically in a different session). Postgres is deliberately NOT updated here
   * — see CLAUDE.md: `commerce.customers.email` is only ever synced by `BootstrapCustomer` reading
   * a fresh, already-verified `email` token claim, the same mechanism phone sync already uses.
   */
  async updateEmail(newEmail: string): Promise<void> {
    if (!this.auth.currentUser) {
      throw new Error('No signed-in user to update an email for.');
    }
    await verifyBeforeUpdateEmail(this.auth.currentUser, newEmail);
  }

  /** Keeps Firebase's own displayName (used by the header greeting) in sync with a Profile-page edit. */
  async updateDisplayName(displayName: string): Promise<void> {
    if (!this.auth.currentUser) {
      throw new Error('No signed-in user.');
    }
    await updateProfile(this.auth.currentUser, { displayName });
    // updateProfile() mutates auth.currentUser in place rather than returning a new object, so the
    // signal must be re-set explicitly for the header greeting to pick up the new displayName.
    this.currentUser.set(this.auth.currentUser);
  }

  async signOut(): Promise<void> {
    await firebaseSignOut(this.auth);
  }

  /**
   * Extracts the pending Google credential from an `auth/account-exists-with-different-credential`
   * error thrown by `signInWithGoogle()` — happens when someone tries Google sign-in with an email
   * that already has an Email/Password account. Returns `null` if the error isn't that specific case.
   */
  credentialFromGoogleError(error: unknown): AuthCredential | null {
    return GoogleAuthProvider.credentialFromError(error as AuthError);
  }

  /**
   * Completes account linking after the above: signs in with the existing Email/Password account,
   * then links the pending Google credential onto it so either method works going forward. Avoids
   * `fetchSignInMethodsForEmail` (unreliable/deprecated on any project with email-enumeration
   * protection, which is on by default for all projects created since Sept 2023) — this reacts to
   * the real sign-in attempt's own error instead of speculatively querying sign-in methods upfront.
   */
  async linkGoogleCredentialViaPassword(
    email: string,
    password: string,
    pendingCredential: AuthCredential
  ): Promise<User> {
    const result = await signInWithEmailAndPassword(this.auth, email, password);
    await linkWithCredential(result.user, pendingCredential);
    return result.user;
  }

  mapFirebaseAuthError(error: unknown): string {
    // Always log the raw error to DevTools — including when the code below IS mapped — so a
    // surprising user-facing message (e.g. a generic one appearing where a specific one was
    // expected) is always traceable back to the exact code/message Firebase actually threw,
    // rather than only being diagnosable for codes we didn't anticipate.
    console.error('[AuthService] Auth error:', error);
    const code = (error as { code?: string })?.code;
    if (code && ERROR_MESSAGES[code]) {
      return ERROR_MESSAGES[code];
    }
    return 'Something went wrong. Please try again.';
  }
}
